import * as THREE from 'three';
import { Ocean } from '../ocean/Ocean';
import { Turbine } from '../turbine/Turbine';
import { LightingManager } from './LightingManager';
import { CameraController, CameraPreset } from './CameraController';
import { SimulationState, NacelleComponentInfo } from '../types';

export class SceneManager {
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  public renderer: THREE.WebGLRenderer;
  public ocean: Ocean;
  public turbine: Turbine;
  public lightingManager: LightingManager;
  public cameraController: CameraController;

  private clock: THREE.Clock;
  private container: HTMLElement;
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;

  // Click callback
  public onComponentSelected?: (info: NacelleComponentInfo | null) => void;
  public onNacelleClicked?: () => void;

  constructor(container: HTMLElement) {
    this.container = container;
    this.clock = new THREE.Clock();
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    // Scene
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x60a5fa, 0.0035);

    // Camera
    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.5, 800);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(this.renderer.domElement);

    // Lighting
    this.lightingManager = new LightingManager(this.scene);

    // Ocean
    this.ocean = new Ocean();
    this.scene.add(this.ocean.mesh);

    // Turbine
    this.turbine = new Turbine();
    this.scene.add(this.turbine.group);

    // Camera Controller
    this.cameraController = new CameraController(this.camera, this.renderer.domElement);

    // Event listeners
    window.addEventListener('resize', this.onResize.bind(this));
    this.renderer.domElement.addEventListener('pointerup', this.onPointerUp.bind(this));

    // Initial lighting setup
    this.lightingManager.updateTime(12, false);
    this.updateOceanLighting();
  }

  public updateOceanLighting() {
    this.ocean.updateLighting(
      this.lightingManager.sunLight.position,
      this.lightingManager.sunLight.color,
      this.lightingManager.moonLight.position,
      this.lightingManager.moonLight.color,
      this.lightingManager.currentSkyColor,
      this.lightingManager.currentDeepColor,
      this.lightingManager.currentShallowColor
    );
  }

  private onResize() {
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  private onPointerUp(event: PointerEvent) {
    // Only handle primary clicks
    if (event.button !== 0) return;

    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);

    // If nacelle is open, test drivetrain internal components first
    if (this.turbine.getShellOpenProgress() > 0.5) {
      const hits = this.raycaster.intersectObjects(this.turbine.drivetrain.interactiveMeshes, false);
      if (hits.length > 0) {
        const topHit = hits[0].object as THREE.Mesh;
        const compId = topHit.userData.componentId;
        if (compId) {
          const info = this.turbine.drivetrain.getComponentInfo(compId);
          this.turbine.drivetrain.highlightComponent(compId);
          if (this.onComponentSelected) {
            this.onComponentSelected(info);
          }
          return;
        }
      }
    }

    // Otherwise test outer nacelle hit
    const nacelleHits = this.raycaster.intersectObject(this.turbine.nacelleMesh, false);
    if (nacelleHits.length > 0) {
      if (this.onNacelleClicked) {
        this.onNacelleClicked();
      }
    }
  }

  public setCameraPreset(preset: CameraPreset) {
    this.cameraController.setPreset(preset, true);
  }

  public resetView() {
    this.cameraController.resetView(true);
  }

  public render(state: SimulationState) {
    const delta = Math.min(this.clock.getDelta(), 0.1);

    // Update animations
    this.ocean.update(delta);
    this.turbine.update(delta);
    this.cameraController.update(delta);

    // Render scene
    this.renderer.render(this.scene, this.camera);
  }
}
